import axios from "axios";

/* =========================================================================
   CLIENTE HTTP
   Laravel se configura con apiPrefix 'v1' en bootstrap/app.php, de modo que
   la API queda en /v1/... y no en /api/v1/... (comprobado contra el
   servidor). En desarrollo Vite hace de proxy hacia 127.0.0.1:8000, así que
   basta una ruta relativa y no hay CORS de por medio.
   ======================================================================== */

export const API_VERSION = import.meta.env.VITE_API_VERSION || "v1";

/* La API de producción, como respaldo del respaldo.
 *
 * `VITE_API_URL` la resuelve Vite AL COMPILAR, así que si el despliegue se
 * hace sin pasarla, queda congelado `/v1` y el panel pide contra su propio
 * dominio —admin.vecipaya.com/v1—, donde no hay API. Y no falla al
 * construir ni al arrancar: falla al intentar entrar, que es cuando ya está
 * publicado. Con esto, olvidar el argumento de build deja el panel
 * funcionando en vez de dejarlo inservible.
 *
 * En desarrollo se mantiene `/v1`, que es lo que el proxy de Vite manda al
 * Laravel local. */
// vecipaya.com desde el 2026-09-14: el dominio anterior dejó de apuntar al servidor.
const API_PRODUCCION = "https://api.vecipaya.com/v1";

export const API_BASE =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? API_PRODUCCION : `/${API_VERSION}`);

const TOKEN_KEY = "vpy.admin.token";
const USER_KEY = "vpy.admin.user";

export const tokenStore = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* almacenamiento bloqueado: la sesión dura lo que la pestaña */
    }
  },
};

export const userStore = {
  get: () => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  set: (user) => {
    try {
      if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
      else localStorage.removeItem(USER_KEY);
    } catch {
      /* ídem */
    }
  },
};

export const http = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  headers: { Accept: "application/json" },
});

http.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // FormData necesita que el navegador ponga el boundary; forzar el
  // Content-Type lo rompe.
  if (config.data instanceof FormData) delete config.headers["Content-Type"];
  return config;
});

/** Se avisa una sola vez por ráfaga: un 401 suele llegar en varias peticiones. */
let avisandoSesionCaida = false;
let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      tokenStore.set(null);
      userStore.set(null);
      if (!avisandoSesionCaida && onUnauthorized) {
        avisandoSesionCaida = true;
        try {
          onUnauthorized();
        } catch (fallo) {
          /*
           * Que el aviso falle no puede cambiar el error de la petición.
           *
           * Sin este `catch`, una excepción aquí dentro sustituía al error de
           * axios: la pantalla recibía un TypeError sin `response` y mostraba
           * "no se pudo conectar con el servidor" ante un 401 que el servidor
           * había contestado perfectamente. Pasó de verdad, y costó encontrarlo
           * justamente porque el síntoma apuntaba al sitio equivocado.
           */
          console.error("Fallo al avisar de la sesión caída", fallo);
        } finally {
          setTimeout(() => {
            avisandoSesionCaida = false;
          }, 3000);
        }
      }
    }

    // Un 4xx es una respuesta del negocio que la pantalla ya muestra con su
    // propio aviso; no merece console.error ni ensuciar la consola.
    if (status && status >= 500) {
      console.error(
        "API error",
        status,
        error.config?.url,
        error.response?.data,
      );
    }

    return Promise.reject(error);
  },
);

/** Mensaje legible a partir de una respuesta de error de Laravel. */
export const errorMessage = (
  error,
  fallback = "Ocurrió un error inesperado.",
) => {
  const data = error?.response?.data;
  if (!error?.response) {
    return "No se pudo conectar con el servidor. Verifica que el backend esté arriba.";
  }
  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) {
    // 422 de Laravel: se listan los campos para que el aviso sirva de algo.
    if (data.errors && typeof data.errors === "object") {
      const detalles = Object.values(data.errors).flat().filter(Boolean);
      if (detalles.length) return detalles.join(" ");
    }
    return data.message;
  }
  if (error?.response?.status === 403)
    return "No tienes permiso para esta acción.";
  if (error?.response?.status === 404) return "El recurso no existe.";
  return fallback;
};

/**
 * Laravel a veces responde `{data: [...]}`, a veces el arreglo pelado y a
 * veces `{data: {data: [...]}}` cuando pagina. Se normaliza para que las
 * pantallas nunca tengan que adivinar.
 */
export const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  for (const key of ["items", "results", "records", "rows"]) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};
